import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatDateTime } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import {
  channelLabel,
  draftStatusLabel,
  draftStatusVariant,
  isCoordinatorQuery,
  recipientLabel,
  sourceLabel,
} from "@/lib/coordinator";
import type { CoordinatorDraft } from "@/services/moderators/generated/model";
import {
  useModeratorsCoordinatorDraftsApproveCreate,
  useModeratorsCoordinatorDraftsRejectCreate,
} from "@/services/moderators/generated/moderators-coordinator/moderators-coordinator";

interface Props {
  draft: CoordinatorDraft;
  /** Minutes pre-filled in the "log my review time" box (COORDINATOR_DEFAULT_REVIEW_MINUTES). */
  defaultReviewMinutes: number;
  /** Show a link to the deal (queue page). */
  showDeal?: boolean;
}

/**
 * WP2.6 — one draft in the coordinator approval queue. A pending draft can be
 * edited, then approved (posted exactly once) or rejected (nothing is sent).
 */
const CoordinatorDraftCard = ({ draft, defaultReviewMinutes, showDeal = false }: Props) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const [text, setText] = useState(draft.originalText);
  const [logTime, setLogTime] = useState(defaultReviewMinutes > 0);
  const [minutes, setMinutes] = useState(String(defaultReviewMinutes || 5));
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ predicate: (q) => isCoordinatorQuery(q.queryKey) });
  const approveMutation = useModeratorsCoordinatorDraftsApproveCreate({ mutation: { onSuccess: refresh } });
  const rejectMutation = useModeratorsCoordinatorDraftsRejectCreate({ mutation: { onSuccess: refresh } });
  const busy = approveMutation.isPending || rejectMutation.isPending;
  const pending = draft.status === "pending";

  const approve = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError(t("coordinator.draft.errors.empty"));
      return;
    }
    const mins = Number(minutes);
    if (logTime && (!Number.isInteger(mins) || mins < 1 || mins > 1440)) {
      setError(t("coordinator.timeLog.errors.minutes"));
      return;
    }
    approveMutation.mutate(
      {
        id: draft.id,
        data: {
          ...(trimmed !== draft.originalText.trim() ? { text: trimmed } : {}),
          ...(logTime ? { logMinutes: mins } : {}),
        },
      },
      {
        onSuccess: () => setError(null),
        onError: (err) => setError(getApiErrorMessage(err, t("coordinator.draft.errors.approveFailed"))),
      },
    );
  };

  const reject = () => {
    rejectMutation.mutate(
      { id: draft.id, data: { reason: reason.trim() } },
      {
        onSuccess: () => setError(null),
        onError: (err) => setError(getApiErrorMessage(err, t("coordinator.draft.errors.rejectFailed"))),
      },
    );
  };

  return (
    <article className="coord-draft">
      <div className="coord-draft__meta">
        <span className={`mod-page__chip mod-page__chip--${draftStatusVariant(draft.status)}`}>
          {draftStatusLabel(draft.status)}
        </span>
        <span>
          {t("coordinator.draft.to", {
            company: draft.companyName,
            role: recipientLabel(draft.recipientRole),
          })}
        </span>
        <span>· {channelLabel(draft.channel)}</span>
        <span>· {sourceLabel(draft.source)}</span>
        <span>· {formatDateTime(draft.createdAt)}</span>
        {showDeal ? (
          <Link to={`/moderator/deals/${draft.dealId}`}>
            {t("coordinator.draft.deal", { id: draft.dealId, title: draft.dealTitle })}
          </Link>
        ) : null}
      </div>
      {draft.title ? <p className="coord-draft__title">{draft.title}</p> : null}

      {pending ? (
        <>
          <label className="mod-modal__label">
            {t("coordinator.draft.textLabel")}
            <textarea
              className="mod-page__textarea"
              rows={4}
              maxLength={4000}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <div className="coord-draft__actions">
            <label className="coord-draft__log">
              <input type="checkbox" checked={logTime} onChange={(e) => setLogTime(e.target.checked)} />
              {t("coordinator.draft.logTime")}
              <input
                type="number"
                min={1}
                max={1440}
                className="mod-page__input coord-draft__minutes"
                value={minutes}
                disabled={!logTime}
                onChange={(e) => setMinutes(e.target.value)}
                aria-label={t("coordinator.timeLog.minutesLabel")}
              />
            </label>
            <button type="button" className="mod-page__btn mod-page__btn--primary" disabled={busy} onClick={approve}>
              {approveMutation.isPending ? t("coordinator.draft.approving") : t("coordinator.draft.approve")}
            </button>
            {!rejecting ? (
              <button
                type="button"
                className="mod-page__btn mod-page__btn--ghost"
                disabled={busy}
                onClick={() => setRejecting(true)}
              >
                {t("coordinator.draft.reject")}
              </button>
            ) : null}
          </div>
          {rejecting ? (
            <div className="coord-draft__actions">
              <input
                type="text"
                className="mod-page__input"
                maxLength={1000}
                placeholder={t("coordinator.draft.rejectReason")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button type="button" className="mod-page__btn mod-page__btn--danger" disabled={busy} onClick={reject}>
                {t("coordinator.draft.confirmReject")}
              </button>
              <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => setRejecting(false)}>
                {t("common:actions.cancel")}
              </button>
            </div>
          ) : null}
          <p className="mod-page__card-meta">{t("coordinator.draft.approvalNote")}</p>
        </>
      ) : (
        <>
          <p className="coord-draft__text">{draft.status === "approved" ? draft.finalText : draft.originalText}</p>
          <p className="mod-page__card-meta">
            {t(draft.status === "approved" ? "coordinator.draft.approvedBy" : "coordinator.draft.rejectedBy", {
              name: draft.reviewedByName ?? "—",
              date: draft.reviewedAt ? formatDateTime(draft.reviewedAt) : "—",
            })}
            {draft.wasEdited ? ` ${t("coordinator.draft.edited")}` : ""}
            {draft.rejectReason ? ` ${t("coordinator.draft.reason", { reason: draft.rejectReason })}` : ""}
          </p>
        </>
      )}
      {error ? <p className="mod-modal__error">{error}</p> : null}
    </article>
  );
};

export default CoordinatorDraftCard;
